<script setup>
import { ref, watch } from 'vue'
import { useNotification } from "@kyvg/vue3-notification";
import { useStore } from 'vuex'
import FormButton from '../Atoms/FormButton.vue'
import FloatingInput from '../ui/FloatingInput.vue'
import Sidebar from '../modals/Sidebar'
import MediaflowService from '@/services/MediaflowService';

const props = defineProps({
  mediaflowId: {
    type: String,
    default: '',
    required: true
  },
  flow: {
    type: Object,
    default: null
  }
})

const store = useStore()
const { notify } = useNotification()

const sidebar = ref()
const error = ref(null)
const processing = ref(false)
const flowName = ref('')

const emit = defineEmits(['flow-updated'])

watch(() => props.flow, (newFlow) => {
  if (newFlow) {
    flowName.value = newFlow.name || ''
  }
}, { immediate: true })

const toggleSidebar = () => sidebar.value.toggleSidebar()

const onReset = () => {
  flowName.value = ''
  error.value = null
  processing.value = false
}

const updateFlow = async () => {
  if (!props.flow || !flowName.value.trim()) return

  processing.value = true
  error.value = null

  try {
    const updatedFlow = await MediaflowService.updateFlow(props.mediaflowId, props.flow._id, flowName.value.trim())
    emit('flow-updated', updatedFlow)
    notify({ group: 'success', text: 'Flow updated successfully' })
    sidebar.value.closeSidebar()
  } catch (err) {
    error.value = err.message || 'Failed to update flow'
    notify({ group: 'error', text: error.value })
  } finally {
    processing.value = false
  }
}

defineExpose({ toggleSidebar })
</script>

<template>
  <Sidebar ref="sidebar" :stage="1" @closed="onReset" width="md:w-[420px]">
    <template v-slot:stage-one>
      <div class="px-4 h-full">
        <h3 class="text-lg font-medium mb-2 mt-3">Edit Flow</h3>
        <p class="text-dark-7 mb-4 text-sm">Update your flow name.</p>
        <div class="mb-3">
          <FloatingInput id="edit-flow-name" label="Flow name *" surface="2" dense v-model="flowName" />
        </div>
        <div class="text-center">
          <FormButton :disabled="!flowName.trim() || processing" @click="updateFlow" type="primary" size="md" class="w-full mt-4 justify-center">
            {{ processing ? 'Saving...' : 'Save' }}
          </FormButton>
          <FormButton type="secondary" size="md" class="w-full mb-3 mt-2 justify-center" @click="toggleSidebar">
            Cancel
          </FormButton>
        </div>
      </div>
    </template>
  </Sidebar>
</template>
